
import { RevButton } from '@ds/components/Button'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSkeleton } from '@ds/components/Skeleton'
import algoliasearch from 'algoliasearch/lite'
import isEmpty from 'lodash/isEmpty'
import { mapGetters } from 'vuex'

import logger from '@logger'
import { trackClick } from '@tracking/events'

import Chart from '../Chart/Chart.vue'
import { groupBy } from '../Table/Table.helpers'
import Table from '../Table/Table.vue'

import translations from './ComparisonResults.translations'

export default {
  components: {
    RevButton,
    Chart,
    RevIllustration,
    RevSkeleton,
    Table,
  },

  props: {
    comparisonConfig: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectedModels: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      models: [],
    }
  },

  async fetch() {
    try {
      this.models = await Promise.all(
        this.selectedModels.map((model) => this.getModelData(model)),
      )
    } catch (error) {
      this.$nuxt.error({
        statusCode: 404,
        message: error.message,
      })
    }
  },

  computed: {
    ...mapGetters({ locale: 'config/locale' }),

    translations: () => translations,

    hasModels() {
      return !isEmpty(this.models)
    },

    modelsFormatted() {
      const modelsFormatted = this.models.map(({ model, offer }) => {
        // Pick the model link for the current locale
        const modelLinkForCurrentLocale = Object.entries(
          model?.model.links,
        ).find(([index]) => index === this.locale)
        const fallBackModelLink = !isEmpty(modelLinkForCurrentLocale)
          ? modelLinkForCurrentLocale[1]
          : null

        return {
          // Use `offer` image in priority, and `model` image as fallback
          image: offer?.image1 || model?.model.image || null,
          hasImage: !isEmpty(offer?.image1) || !isEmpty(model?.model.image),
          name: model?.model.name,
          price: offer?.price,
          currency: offer?.currency,
          // Use `offer` link in priority, and `model` link for current locale as fallback
          link: offer?.link_grade_v2 || fallBackModelLink,
        }
      })

      return modelsFormatted
    },

    specificationsFormatted() {
      const specificationsEnriched = this.models
        .map(({ model: { model, specifications } }) =>
          specifications.groups.map((group) => ({
            ...group,
            productId: model.name,
          })),
        )
        .flat()

      const reviewsEnriched = this.models.map(
        ({ model: { model, reviewsRating } }) => ({
          ...reviewsRating,
          productId: model.name,
        }),
      )

      // `label` is used below as object key
      // eslint-disable-next-line no-unused-vars
      const { label } = this.models[0].model.reviewsRating

      const specificationsAndReviewsByLabel = {
        [label]: reviewsEnriched,
        ...groupBy(specificationsEnriched, 'label'),
      }

      return specificationsAndReviewsByLabel
    },

    chartData() {
      const modelsScores = this.models.map(({ model }) => [
        model.reviewsRating,
        ...model.specifications.groups,
      ])
      const modelsNames = this.models.map(({ model }) => model.model.name)

      return { modelsNames, modelsScores }
    },
  },

  watch: {
    selectedModels: '$fetch',
  },

  methods: {
    async getModelData(model) {
      const modelData = await Promise.all(
        this.comparisonConfig.map(async ({ apiKey, queries }) => {
          const algoliaClient = algoliasearch(this.$config.ALGOLIA_ID, apiKey)

          const customConfigForModel = this.getCustomModelConfig({
            model,
            queries,
          })

          try {
            const { results } = await algoliaClient.multipleQueries(
              customConfigForModel,
            )

            return results
          } catch (error) {
            logger.error('Error fetching model data from catalog', {
              error,
              modelName: model.compareName,
            })

            return []
          }
        }),
      )

      return {
        model: modelData.flat().find((result) => result.index.includes('model'))
          ?.hits[0],

        offer: modelData.flat().find((result) => result.index.includes('price'))
          ?.hits[0],
      }
    },

    getCustomModelConfig({ model, queries }) {
      const { compareName, catalogName } = model

      // We need to update the config with the selected model name
      const indexPriceConfig = this.replaceModelNameInConfig({
        modelName: catalogName,
        index: 'price',
        queries,
      })
      const indexComparisonConfig = this.replaceModelNameInConfig({
        modelName: compareName,
        index: 'model',
        queries,
      })

      return [...indexPriceConfig, ...indexComparisonConfig]
    },

    replaceModelNameInConfig({ modelName, index, queries }) {
      const configToUpdate = queries.filter((query) =>
        query.indexName.includes(index),
      )

      const filtersToUpdate = configToUpdate.flatMap(
        ({ params }) => params.facetFilters,
      )
      const nameToReplace = index === 'price' ? 'CATALOG_NAME' : 'COMPARE_NAME'

      const facetFiltersWithModelName = filtersToUpdate.map((filter) =>
        filter.includes(nameToReplace)
          ? filter.replace(nameToReplace, modelName)
          : filter,
      )

      return configToUpdate.map((config) => ({
        indexName: config.indexName,
        params: {
          ...config.params,
          facetFilters: facetFiltersWithModelName,
        },
      }))
    },

    handleModelCtaClick(modelName) {
      trackClick({
        name: `buy_${modelName}`,
      })
    },
  },
}
